/**
 * Local progress store: per-level attempts, best progress, completions.
 * localStorage for Phase 1; moves behind a profile/save-slot abstraction with
 * IndexedDB + cloud sync in Phase 5/6 (see FEATURE_MATRIX §8).
 */
export interface LevelProgress {
  attempts: number;
  /** Best progress 0..1 (normal mode). */
  best: number;
  completions: number;
  totalJumps: number;
}

import { mirrorKey } from './save-mirror';

const KEY = 'shape-shifters.progress.v1';

type ProgressMap = Record<string, LevelProgress>;

/** In-memory fallback when localStorage is unavailable (headless tests). */
let memory: ProgressMap | null = null;

function load(): ProgressMap {
  if (typeof localStorage === 'undefined') return (memory ??= {});
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as ProgressMap;
  } catch (e) {
    console.warn('Progress load failed; starting fresh.', e);
    return {};
  }
}

function store(map: ProgressMap): void {
  if (typeof localStorage === 'undefined') {
    memory = map;
    return;
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
    mirrorKey(KEY);
  } catch (e) {
    console.warn('Progress save failed (storage unavailable?).', e);
  }
}

export function getProgress(levelId: string): LevelProgress {
  return (
    load()[levelId] ?? {
      attempts: 0,
      best: 0,
      completions: 0,
      totalJumps: 0,
    }
  );
}

export function updateProgress(levelId: string, update: (p: LevelProgress) => void): LevelProgress {
  const map = load();
  const p = map[levelId] ?? { attempts: 0, best: 0, completions: 0, totalJumps: 0 };
  update(p);
  map[levelId] = p;
  store(map);
  return p;
}
